/**
 * AI-Ready-Check / Reifegrad-Assessment (CompanyBrain Welle 3b, BEN-107).
 * Hooks fuer Rahmenwerk (Fragenkatalog), Liste/Detail/Anlegen/Bearbeiten/Status,
 * lokale KI-Verdichtung + Statistik. Persistenz Orchestrator (Postgres, tenant+user-scoped).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";
import type { Ampel } from "./kundencockpit";

export type ScaleItem = { value: number; label: string };
export type LevelDef = { min: number; max: number; stufe: number; name: string; beschreibung: string };
export type FrameworkQuestion = { id: string; text: string; hint: string };
export type FrameworkDimension = {
  key: string; name: string; beschreibung: string; weight: number; questions: FrameworkQuestion[];
};
export type Framework = {
  tenant_id: string;
  version: string;
  scale: ScaleItem[];
  levels: LevelDef[];
  dimensions: FrameworkDimension[];
  hinweis: string;
};

export type QuestionResult = { id: string; text: string; score: number | null; note: string };
export type DimensionResult = {
  key: string; name: string; beschreibung?: string;
  score: number | null; ampel: Ampel; answered: number; total_questions: number;
  questions: QuestionResult[];
};
export type LevelResult = { stufe: number; name: string; beschreibung: string };
export type Recommendation = {
  dimension: string; dimension_name: string; score: number | null; severity: string; text: string;
  intervention: { code: string; name: string; route: string; score: number } | null;
};
export type AssessmentResult = {
  dimensions: DimensionResult[];
  overall_score: number | null;
  overall_ampel: Ampel;
  level: LevelResult;
  recommendations: Recommendation[];
  next_steps: string[];
  answered_any: boolean;
};

export type Assessment = {
  id: string;
  user_sub?: string;
  title: string;
  scope: string;
  status: "entwurf" | "abgeschlossen" | string;
  answers: Record<string, Record<string, { score?: number | null; note?: string } | number>>;
  result: AssessmentResult;
  overall_score: number | null;
  ai_summary: string;
  meta: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
};

export type AssessmentSummary = {
  id: string;
  title: string;
  scope: string;
  status: string;
  overall_score: number | null;
  overall_ampel?: Ampel;
  level?: LevelResult;
  ai_summary?: string;
  dimensions: { key: string; name: string; score: number | null; ampel: Ampel }[];
  created_at: string | null;
  updated_at: string | null;
};

export type AiReadyStats = {
  total: number; abgeschlossen: number; entwurf: number;
  latest: { id: string; title?: string; scope?: string; status?: string; overall_score?: number | null; updated_at?: string | null } | null;
};

export type AiReadyListResponse = {
  tenant_id: string; count: number; status: string; scope: string;
  items: AssessmentSummary[]; persistent: boolean; stats: AiReadyStats;
};

export type AssessmentInput = {
  title?: string;
  scope?: string;
  status?: "entwurf" | "abgeschlossen";
  answers?: Record<string, Record<string, { score?: number | null; note?: string }>>;
  meta?: Record<string, unknown>;
};

export function useAiReadyFramework() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["ai-ready-framework", effectiveTenantId],
    queryFn: () => api.get<Framework>("/ai-ready-check/framework"),
    enabled: Boolean(effectiveTenantId),
    staleTime: 5 * 60_000,
  });
}

export function useAiReadyList(status: "all" | "entwurf" | "abgeschlossen" = "all") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["ai-ready-list", effectiveTenantId, status],
    queryFn: () => api.get<AiReadyListResponse>(`/ai-ready-check?status=${status}&limit=200`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 15_000,
  });
}

export function useAiReadyDetail(id: string | null) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["ai-ready-detail", effectiveTenantId, id],
    queryFn: () => api.get<{ assessment: Assessment }>(`/ai-ready-check/${encodeURIComponent(id!)}`),
    enabled: Boolean(effectiveTenantId) && Boolean(id),
    retry: 1,
  });
}

export function useCreateAiReady() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssessmentInput) =>
      api.post<{ ok: boolean; assessment: Assessment; persistent: boolean }>("/ai-ready-check", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-ready-list"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
      qc.invalidateQueries({ queryKey: ["kundencockpit"] });
    },
  });
}

export function useUpdateAiReady() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssessmentInput }) =>
      api.put<{ ok: boolean; assessment: Assessment }>(`/ai-ready-check/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-ready-list"] });
      qc.invalidateQueries({ queryKey: ["ai-ready-detail"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}

export function useSetAiReadyStatus() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "entwurf" | "abgeschlossen" }) =>
      api.post<{ ok: boolean; assessment: Assessment }>(`/ai-ready-check/${encodeURIComponent(id)}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-ready-list"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
      qc.invalidateQueries({ queryKey: ["kundencockpit"] });
    },
  });
}

export type AiSummaryResponse = { summary: string; engine: string; grounded: boolean; hinweis?: string };

export function useAiReadySummary() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AiSummaryResponse>(`/ai-ready-check/${encodeURIComponent(id)}/ai-summary`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-ready-detail"] });
      qc.invalidateQueries({ queryKey: ["ai-ready-list"] });
    },
  });
}

export function useDeleteAiReady() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean; id: string }>(`/ai-ready-check/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-ready-list"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}

export const STATUS_LABEL: Record<string, string> = {
  entwurf: "Entwurf",
  abgeschlossen: "Abgeschlossen",
};

export const SEVERITY_LABEL: Record<string, string> = {
  hoch: "Hoch",
  mittel: "Mittel",
};
